import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuthController, useDataSource } from "@camberi/firecms";
import versionsCollection from "../../../Collections/versionsCollection";
import assetsCollection from "../../../Collections/assetsCollection";
import VersionMenu from "../Components/VersionMenu";
import { getByPath, isFreshSignedUrl, isHttpUrl, isSignableStoragePath, isSignedUrlFieldPath, normalizeFieldPath, useWindowSize } from "./playerUtils";
import {
	getAvailableVideoSources,
	getDefaultPreviewAssetId,
	getMatchingPreviewAssetId,
	getPreferredImageSource,
	getPreviewAssetId,
	getPreviewAssetSelectionId,
	getPreviewAssetUploadId,
	getPreviewAssets,
	getPreviewSourceContext,
	pickBestVideoSource,
	sortPreviewAssetsBySourceFileName,
	toIdArray,
} from "./previewAssetUtils";
import { usePlayerState } from "./usePlayerState";
import { PlayerStage } from "./PlayerStage";
import { PlayerComparisonStage } from "./PlayerComparisonStage";
import { PlayerControls } from "./PlayerControls";
import { PreviewAssetTabs } from "./PreviewAssetTabs";
import { CompareVersionMenu } from "./CompareVersionMenu";
type PlayerDialogOpenOptions = { compareVersionId?: string; comparePreviewAssetId?: string; currentTimeSeconds?: number | string | null; fromQuery?: boolean };
export type TreeViewMenuModalPlayerHandle = { openDialog: (assetIdInput: string, versionId?: string, previewAssetId?: string, inputOnCloseCallback?: any, options?: PlayerDialogOpenOptions) => void; closeDialog: () => void };
const MemoVersionMenu = React.memo(VersionMenu);
function parseNumberish(value: unknown): number | null {
	if (typeof value === "number" && Number.isFinite(value)) return value;
	if (typeof value === "string") {
		const cleaned = value.trim().replace("%", "");
		if (!cleaned) return null;
		const parsed = Number(cleaned);
		if (Number.isFinite(parsed)) return parsed;
	}
	return null;
}
function parseQueryTimeSeconds(value: any): number | null {
	const parsed = parseNumberish(value);
	if (parsed === null || parsed < 0) {
		return null;
	}
	return parsed;
}
function formatQueryTimeSeconds(value: number) {
	const safeValue = Math.max(0, Number.isFinite(value) ? value : 0);
	const rounded = Math.round(safeValue * 1000) / 1000;
	return rounded.toFixed(3).replace(/\.?0+$/, "");
}
function clampNumber(value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max);
}
function replaceBrowserQueryParams(updates: Record<string, any>) {
	if (typeof window === "undefined" || !("URLSearchParams" in window)) {
		return;
	}
	const searchParams = new URLSearchParams(window.location.search);
	Object.entries(updates).forEach(([key, value]) => {
		const text = value === undefined || value === null ? "" : String(value).trim();
		if (text.length) {
			searchParams.set(key, text);
		} else {
			searchParams.delete(key);
		}
	});
	const nextSearch = searchParams.toString();
	const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
	const currentUrl = `${window.location.pathname}${window.location.search}`;
	if (nextUrl !== currentUrl) {
		window.history.replaceState(null, "", nextUrl);
	}
}
function parseOverscanValue(value: unknown): number | null {
	const parsed = parseNumberish(value);
	if (parsed === null || parsed < 0) return null;
	if (typeof value === "string" && value.includes("%")) {
		return parsed / 100;
	}
	if (parsed > 1) {
		return parsed / 100;
	}
	return parsed;
}
function resolveOverscanFromResolution(resolution?: any): number | null {
	const dataX = Number(resolution?.data?.x) || 0;
	const dataY = Number(resolution?.data?.y) || 0;
	const dispX = Number(resolution?.disp?.x) || 0;
	const dispY = Number(resolution?.disp?.y) || 0;
	const ratios: number[] = [];
	if (dataX > 0 && dispX > 0 && dataX > dispX) {
		ratios.push(dataX / dispX - 1);
	}
	if (dataY > 0 && dispY > 0 && dataY > dispY) {
		ratios.push(dataY / dispY - 1);
	}
	if (!ratios.length) return null;
	return Math.max(...ratios);
}
function resolveOverscanPercentageFromVersionMetadata(versionMetadata?: any): number {
	const shotMetadata = versionMetadata?.render_metadata?.shot;
	const camera = shotMetadata?.camera;
	const resolution = shotMetadata?.resolution;
	const pathTracerOverscan = parseOverscanValue(camera?.PathTracer?.overscanPercent);
	if (pathTracerOverscan !== null && pathTracerOverscan > 0) {
		return pathTracerOverscan;
	}
	const cameraOverscan = parseOverscanValue(camera?.overscanPercentage);
	if (cameraOverscan !== null && cameraOverscan > 0) {
		return cameraOverscan;
	}
	const resolutionOverscan = resolveOverscanFromResolution(resolution);
	if (resolutionOverscan !== null && resolutionOverscan > 0) {
		return resolutionOverscan;
	}
	return 0;
}
function firstFiniteNumber(...values: any[]): number {
	for (const value of values) {
		const parsed = parseNumberish(value);
		if (parsed !== null && parsed > 0) {
			return parsed;
		}
	}
	return 0;
}
function getPreviewAspectRatio(previewAssetInfo?: any, versionMetadata?: any): number {
	const shotResolution = versionMetadata?.render_metadata?.shot?.resolution;
	const candidates: Array<[any, any]> = [
		[previewAssetInfo?.metadata?.blob?.width, previewAssetInfo?.metadata?.blob?.height],
		[previewAssetInfo?.metadata?.blob?.videoWidth, previewAssetInfo?.metadata?.blob?.videoHeight],
		[previewAssetInfo?.metadata?.width, previewAssetInfo?.metadata?.height],
		[previewAssetInfo?.metadata?.videoWidth, previewAssetInfo?.metadata?.videoHeight],
		[previewAssetInfo?.width, previewAssetInfo?.height],
		[previewAssetInfo?.videoWidth, previewAssetInfo?.videoHeight],
		[previewAssetInfo?.source?.width, previewAssetInfo?.source?.height],
		[previewAssetInfo?.metadata?.streams?.[0]?.width, previewAssetInfo?.metadata?.streams?.[0]?.height],
		[shotResolution?.data?.x, shotResolution?.data?.y],
		[shotResolution?.disp?.x, shotResolution?.disp?.y],
	];
	for (const [rawWidth, rawHeight] of candidates) {
		const width = firstFiniteNumber(rawWidth);
		const height = firstFiniteNumber(rawHeight);
		if (width > 0 && height > 0) {
			return width / height;
		}
	}
	return 16 / 9;
}
function getFirstVideoPreviewAssetId(values: any) {
	const sortedPreviewAssets = sortPreviewAssetsBySourceFileName(getPreviewAssets(values), values);
	for (const previewAsset of sortedPreviewAssets) {
		const previewAssetId = getPreviewAssetId(previewAsset);
		const sourceContext = getPreviewSourceContext(values, previewAssetId);
		if (previewAssetId && sourceContext?.id && sourceContext?.isVideo) {
			return previewAssetId;
		}
	}
	return "";
}
function getFirstRenderablePreviewAssetId(values: any) {
	const sortedPreviewAssets = sortPreviewAssetsBySourceFileName(getPreviewAssets(values), values);
	for (const previewAsset of sortedPreviewAssets) {
		const previewAssetId = getPreviewAssetId(previewAsset);
		const sourceContext = getPreviewSourceContext(values, previewAssetId);
		if (previewAssetId && sourceContext?.id) {
			return previewAssetId;
		}
	}
	return "";
}

type FrameDownloadFormat = "png" | "jpeg";

const GHOST_VIDEO_LOAD_TIMEOUT_MS = 20000;
const GHOST_VIDEO_SEEK_TIMEOUT_MS = 12000;
const GHOST_VIDEO_FRAME_TIMEOUT_MS = 5000;

function getVideoElementErrorMessage(video: HTMLVideoElement) {
	const code = video.error?.code;

	if (code === 1) return "The browser aborted loading the source video.";
	if (code === 2) return "The browser could not reach the source video.";
	if (code === 3) return "The browser could not decode the source video.";
	if (code === 4) return "The source video format is not playable in this browser.";

	return "The source video could not be loaded.";
}

function waitForGhostVideoMetadata(video: HTMLVideoElement) {
	if (video.readyState >= 1) {
		return Promise.resolve();
	}

	return new Promise<void>((resolve, reject) => {
		let timeoutId: number | undefined;

		const cleanup = () => {
			if (typeof timeoutId !== "undefined") {
				window.clearTimeout(timeoutId);
			}

			video.removeEventListener("loadedmetadata", handleReady);
			video.removeEventListener("loadeddata", handleReady);
			video.removeEventListener("canplay", handleReady);
			video.removeEventListener("error", handleError);
		};

		const handleReady = () => {
			if (video.readyState >= 1) {
				cleanup();
				resolve();
			}
		};

		const handleError = () => {
			cleanup();
			reject(new Error(getVideoElementErrorMessage(video)));
		};

		timeoutId = window.setTimeout(() => {
			cleanup();
			reject(new Error("Timed out while loading source video metadata."));
		}, GHOST_VIDEO_LOAD_TIMEOUT_MS);

		video.addEventListener("loadedmetadata", handleReady);
		video.addEventListener("loadeddata", handleReady);
		video.addEventListener("canplay", handleReady);
		video.addEventListener("error", handleError);
	});
}

function waitForGhostVideoDecodedFrame(video: HTMLVideoElement, timeoutMs: number = GHOST_VIDEO_FRAME_TIMEOUT_MS) {
	const hasDecodedFrame = () => video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0;

	if (hasDecodedFrame()) {
		return Promise.resolve();
	}

	return new Promise<void>((resolve, reject) => {
		let timeoutId: number | undefined;

		const cleanup = () => {
			if (typeof timeoutId !== "undefined") {
				window.clearTimeout(timeoutId);
			}

			video.removeEventListener("loadeddata", handleReady);
			video.removeEventListener("canplay", handleReady);
			video.removeEventListener("canplaythrough", handleReady);
			video.removeEventListener("seeked", handleReady);
			video.removeEventListener("error", handleError);
		};

		const handleReady = () => {
			if (hasDecodedFrame()) {
				cleanup();
				resolve();
			}
		};

		const handleError = () => {
			cleanup();
			reject(new Error(getVideoElementErrorMessage(video)));
		};

		timeoutId = window.setTimeout(() => {
			cleanup();
			reject(new Error("Timed out while waiting for the source video frame to decode."));
		}, timeoutMs);

		video.addEventListener("loadeddata", handleReady);
		video.addEventListener("canplay", handleReady);
		video.addEventListener("canplaythrough", handleReady);
		video.addEventListener("seeked", handleReady);
		video.addEventListener("error", handleError);
	});
}

function waitForGhostVideoFramePresentation(video: HTMLVideoElement) {
	const requestVideoFrameCallback = (video as any).requestVideoFrameCallback;

	if (typeof requestVideoFrameCallback === "function") {
		return new Promise<void>((resolve) => {
			let resolved = false;

			const timeoutId = window.setTimeout(() => {
				if (!resolved) {
					resolved = true;
					resolve();
				}
			}, GHOST_VIDEO_FRAME_TIMEOUT_MS);

			requestVideoFrameCallback.call(video, () => {
				if (!resolved) {
					resolved = true;
					window.clearTimeout(timeoutId);
					resolve();
				}
			});
		});
	}

	return new Promise<void>((resolve) => {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				resolve();
			});
		});
	});
}

function seekGhostVideo(video: HTMLVideoElement, timeSeconds: number) {
	return new Promise<void>((resolve, reject) => {
		let timeoutId: number | undefined;

		const cleanup = () => {
			if (typeof timeoutId !== "undefined") {
				window.clearTimeout(timeoutId);
			}

			video.removeEventListener("seeked", handleSeeked);
			video.removeEventListener("loadeddata", handleLoadedData);
			video.removeEventListener("error", handleError);
		};

		const finishIfReady = () => {
			if (video.readyState >= 2) {
				cleanup();
				resolve();
			}
		};

		const handleSeeked = () => {
			finishIfReady();
		};

		const handleLoadedData = () => {
			finishIfReady();
		};

		const handleError = () => {
			cleanup();
			reject(new Error(getVideoElementErrorMessage(video)));
		};

		timeoutId = window.setTimeout(() => {
			cleanup();
			reject(new Error("Timed out while seeking the source video to the current frame."));
		}, GHOST_VIDEO_SEEK_TIMEOUT_MS);

		video.addEventListener("seeked", handleSeeked);
		video.addEventListener("loadeddata", handleLoadedData);
		video.addEventListener("error", handleError);

		try {
			video.currentTime = timeSeconds;
		} catch (error) {
			cleanup();
			reject(error);
		}

		requestAnimationFrame(finishIfReady);
	});
}

function getFrameDownloadMimeType(format: FrameDownloadFormat) {
	return format === "jpeg" ? "image/jpeg" : "image/png";
}

function getFrameDownloadExtension(format: FrameDownloadFormat) {
	return format === "jpeg" ? "jpg" : "png";
}

function canvasToBlob(canvas: HTMLCanvasElement, format: FrameDownloadFormat) {
	return new Promise<Blob>((resolve, reject) => {
		try {
			canvas.toBlob(
				(blob) => {
					if (blob) {
						resolve(blob);
					} else {
						reject(new Error("The browser could not encode the current frame image."));
					}
				},
				getFrameDownloadMimeType(format),
				format === "jpeg" ? 0.95 : undefined
			);
		} catch (error) {
			reject(error);
		}
	});
}

async function createFrameBlobFromSourceVideo(sourceUrl: string, timeSeconds: number, format: FrameDownloadFormat) {
	const video = document.createElement("video");

	video.crossOrigin = "anonymous";
	video.muted = true;
	video.playsInline = true;
	video.preload = "auto";
	video.disablePictureInPicture = true;
	video.style.position = "fixed";
	video.style.left = "-100000px";
	video.style.top = "0";
	video.style.width = "1px";
	video.style.height = "1px";
	video.style.opacity = "0";
	video.style.pointerEvents = "none";

	document.body.appendChild(video);

	try {
		video.src = sourceUrl;
		video.load();

		await waitForGhostVideoMetadata(video);

		const duration = Number(video.duration);
		const targetTime = Number.isFinite(duration) && duration > 0 ? clampNumber(timeSeconds, 0, Math.max(0, duration - 0.001)) : Math.max(0, timeSeconds || 0);

		if (Math.abs((Number(video.currentTime) || 0) - targetTime) > 0.002) {
			await seekGhostVideo(video, targetTime);
		} else {
			await waitForGhostVideoDecodedFrame(video);
		}

		await waitForGhostVideoDecodedFrame(video);
		await waitForGhostVideoFramePresentation(video);

		const width = Number(video.videoWidth) || 0;
		const height = Number(video.videoHeight) || 0;

		if (width <= 0 || height <= 0) {
			throw new Error("The source video did not expose usable frame dimensions.");
		}

		const canvas = document.createElement("canvas");
		canvas.width = width;
		canvas.height = height;

		const context = canvas.getContext("2d");

		if (!context) {
			throw new Error("The browser could not create a 2D canvas for frame export.");
		}

		context.drawImage(video, 0, 0, width, height);

		return await canvasToBlob(canvas, format);
	} finally {
		video.pause();
		video.removeAttribute("src");
		video.load();
		video.remove();
	}
}

function downloadBlob(blob: Blob, filename: string) {
	const objectUrl = URL.createObjectURL(blob);
	const link = document.createElement("a");

	link.href = objectUrl;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	link.remove();

	window.setTimeout(() => {
		URL.revokeObjectURL(objectUrl);
	}, 1000);
}

function sanitizeFrameDownloadFilename(value: any) {
	const text = String(value || "")
		.trim()
		.replace(/[\\/:*?"<>|]+/g, "_")
		.replace(/\s+/g, "_")
		.replace(/_+/g, "_")
		.replace(/^_+|_+$/g, "");

	return text || "frame";
}

function getCurrentPlayerTimeSeconds(playerState: any) {
	const player = playerState?.playerRef?.current?.getInternalPlayer?.();
	const currentTime = Number(player?.currentTime);

	if (Number.isFinite(currentTime) && currentTime >= 0) {
		return currentTime;
	}

	const duration = Number(playerState?.duration);
	const played = Number(playerState?.progress?.played);

	if (Number.isFinite(duration) && duration > 0 && Number.isFinite(played) && played >= 0) {
		return duration * played;
	}

	return 0;
}

function buildFrameDownloadFilename(assetName: any, versionId: any, previewAssetId: any, timeSeconds: number, format: FrameDownloadFormat) {
	const safeAssetName = sanitizeFrameDownloadFilename(assetName);
	const safeVersionId = sanitizeFrameDownloadFilename(versionId);
	const safePreviewAssetId = sanitizeFrameDownloadFilename(previewAssetId);
	const frameTime = Math.max(0, Number.isFinite(timeSeconds) ? timeSeconds : 0)
		.toFixed(3)
		.replace(".", "_");

	return `${safeAssetName}_${safeVersionId}_${safePreviewAssetId}_${frameTime}s.${getFrameDownloadExtension(format)}`;
}

function getFrameDownloadErrorMessage(error: any) {
	const message = String(error?.message || error || "");

	if (message.toLowerCase().includes("tainted") || message.toLowerCase().includes("security")) {
		return "The source video loaded, but the browser blocked canvas export because the source URL is missing CORS headers.";
	}

	return message || "Could not download the current frame.";
}
export const TreeViewMenuModalPlayer = React.forwardRef<TreeViewMenuModalPlayerHandle, {}>(function TreeViewMenuModalPlayer({}, ref) {
	const authController = useAuthController();
	const dataSource = useDataSource();
	const [assetId, setAssetId] = useState<string>("");
	const [open, setOpen] = useState<boolean>(false);
	const [filterSelects, setFilterSelects] = useState<boolean>(false);
	const curLclOverwrites = useRef<any>({});
	const windowSize = useWindowSize();
	const [overlaySize, setOverlaySize] = useState({ width: 0, height: 0 });
	const [versionId, setVersionId] = useState<string>("");
	const [version, setVersion] = useState<any>({});
	const versionListener = useRef<any>();
	const curPrefVersion = useRef<any>();
	const [compareEnabled, setCompareEnabled] = useState<boolean>(false);
	const [compareVersionId, setCompareVersionId] = useState<string>("");
	const [compareVersion, setCompareVersion] = useState<any>({});
	const compareVersionListener = useRef<any>();
	const [queryComparePreviewAssetId, setQueryComparePreviewAssetId] = useState<string>("");
	const [pendingQueryTimeSeconds, setPendingQueryTimeSeconds] = useState<number | null>(null);
	const suppressInitialQueryAutoplayRef = useRef<boolean>(false);
	const [curVideoUrl, setCurVideoUrl] = useState<string>("");
	const curVideoSourceKey = useRef<string>("");
	const requestedSign = useRef<Set<string>>(new Set());
	const [compareVideoUrl, setCompareVideoUrl] = useState<string>("");
	const compareVideoSourceKey = useRef<string>("");
	const compareRequestedSign = useRef<Set<string>>(new Set());
	const [asset, setAsset] = useState<any>({});
	const assetRef = useRef<any>({});
	const [assetNotLoadable, setAssetNotLoadable] = useState<boolean>(false);
	const assetListener = useRef<any>();
	const onCloseCallback = useRef<any>(null);
	const [curPreviewAssetId, setCurPreviewAssetId] = useState<string>("");
	const curPreviewAssetIdRef = useRef<string>("");
	const [desiredVideoQuality, setDesiredVideoQuality] = useState<number>(2160);
	const [, setOverlayVisible] = useState<boolean>(false);
	const [infoState, setInfoState] = useState<"none" | "compact" | "metadata">("none");
	const [gridState, setGridState] = useState<"none" | "grid" | "overscangrid">("none");
	const [overscanZoomEnabled, setOverscanZoomEnabled] = useState<boolean>(false);
	const [videoFlipped, setVideoFlipped] = useState<boolean>(false);
	const [frameDownloadBusy, setFrameDownloadBusy] = useState<boolean>(false);
	const [curFiltered, setCurFiltered] = useState<any[]>([]);
	const [, setDummy] = useState<any>([]);
	const setCurrentPreviewAssetId = React.useCallback((nextPreviewAssetId: string) => {
		const safePreviewAssetId = nextPreviewAssetId || "";
		curPreviewAssetIdRef.current = safePreviewAssetId;
		setCurPreviewAssetId(safePreviewAssetId);
	}, []);
	useEffect(() => {
		curPreviewAssetIdRef.current = curPreviewAssetId;
	}, [curPreviewAssetId]);
	const versionValues = version?.values || {};
	const sourceContext = getPreviewSourceContext(versionValues, curPreviewAssetId);
	const previewAssetInfo = sourceContext?.assetInfo;
	const previewAssetIsVideo = Boolean(sourceContext?.isVideo);
	const frameAccurateTimingAvailable = Boolean(sourceContext?.isFrameAccurate);
	const compareVersionValues = compareVersion?.values || {};
	const comparePreviewAssetId = React.useMemo(() => {
		if (!compareEnabled || !compareVersionId) {
			return "";
		}
		if (queryComparePreviewAssetId.length) {
			const queryCompareSourceContext = getPreviewSourceContext(compareVersionValues, queryComparePreviewAssetId);
			const compareValuesLoaded = Boolean(compareVersion?.id || Object.keys(compareVersionValues || {}).length);
			if (!compareValuesLoaded || queryCompareSourceContext?.id) {
				return queryComparePreviewAssetId;
			}
		}
		const matchedPreviewAssetId = curPreviewAssetId ? getMatchingPreviewAssetId({ primaryVersionValues: versionValues, compareVersionValues, primaryPreviewAssetId: curPreviewAssetId }) : "";
		if (matchedPreviewAssetId) {
			return matchedPreviewAssetId;
		}
		if (!previewAssetIsVideo) {
			const firstVideoPreviewAssetId = getFirstVideoPreviewAssetId(compareVersionValues);
			if (firstVideoPreviewAssetId) {
				return firstVideoPreviewAssetId;
			}
		}
		const defaultComparePreviewAssetId = getDefaultPreviewAssetId(compareVersionValues);
		if (defaultComparePreviewAssetId) {
			return defaultComparePreviewAssetId;
		}
		return getFirstRenderablePreviewAssetId(compareVersionValues);
	}, [compareEnabled, compareVersionId, compareVersion, queryComparePreviewAssetId, curPreviewAssetId, versionValues, compareVersionValues, previewAssetIsVideo]);
	const compareSourceContext = getPreviewSourceContext(compareVersionValues, comparePreviewAssetId);
	const comparePreviewAssetInfo = compareSourceContext?.assetInfo;
	const comparePreviewAssetIsVideo = Boolean(compareSourceContext?.isVideo);
	const compareFrameAccurateTimingAvailable = Boolean(compareSourceContext?.isFrameAccurate);
	const shouldSwapComparisonSides = Boolean(compareEnabled && compareVersionId.length > 0 && !previewAssetIsVideo && comparePreviewAssetIsVideo);
	const controlledPreviewAssetInfo = shouldSwapComparisonSides ? comparePreviewAssetInfo : previewAssetInfo;
	const controlledFrameAccurateTimingAvailable = shouldSwapComparisonSides ? compareFrameAccurateTimingAvailable : frameAccurateTimingAvailable;
	const playerState = usePlayerState({ previewAssetInfo: controlledPreviewAssetInfo, frameAccurateTimingAvailable: controlledFrameAccurateTimingAvailable });
	const versionMetadata = version?.values?.version_metadata;
	const compareVersionMetadata = compareVersion?.values?.version_metadata;
	const controlledVersionMetadata = shouldSwapComparisonSides ? compareVersionMetadata : versionMetadata;
	const overscanPercentage = resolveOverscanPercentageFromVersionMetadata(controlledVersionMetadata);
	const canToggleOverscanZoom = overscanPercentage > 0;
	const effectiveOverscanZoomEnabled = overscanZoomEnabled && overscanPercentage > 0;
	const overscanZoom = effectiveOverscanZoomEnabled ? 1 + overscanPercentage : 1;
	const allPreviewAssets = getPreviewAssets(versionValues);
	const previewAssets = sortPreviewAssetsBySourceFileName(allPreviewAssets, versionValues);
	const syncPlayerQuery = React.useCallback(
		(updates: Record<string, any>) => {
			if (!open) {
				return;
			}
			replaceBrowserQueryParams(updates);
		},
		[open]
	);
	const commitCurrentTimeToQuery = React.useCallback(() => {
		const player = playerState.playerRef.current?.getInternalPlayer?.();
		const currentTime = Number(player?.currentTime);
		if (!Number.isFinite(currentTime) || currentTime < 0) {
			return;
		}
		syncPlayerQuery({ t: formatQueryTimeSeconds(currentTime) });
	}, [playerState.playerRef, syncPlayerQuery]);
	const unloadCurrentVideo = React.useCallback(() => {
		curVideoSourceKey.current = "";
		playerState.pause(true);
		playerState.setBuffering(true);
		setCurVideoUrl((prev) => {
			if (!prev.length) {
				return prev;
			}
			return "";
		});
	}, [playerState.pause, playerState.setBuffering]);
	const clearCurrentVideoForNonVideo = React.useCallback(() => {
		curVideoSourceKey.current = "";
		setCurVideoUrl((prev) => {
			if (!prev.length) {
				return prev;
			}
			return "";
		});
	}, []);
	const unloadCompareVideo = React.useCallback(() => {
		compareVideoSourceKey.current = "";
		setCompareVideoUrl((prev) => {
			if (!prev.length) {
				return prev;
			}
			return "";
		});
	}, []);
	const resetCompareState = React.useCallback(() => {
		compareRequestedSign.current = new Set();
		compareVideoSourceKey.current = "";
		setCompareEnabled(false);
		setCompareVersionId("");
		setCompareVersion({});
		setCompareVideoUrl("");
		setQueryComparePreviewAssetId("");
	}, []);
	const handleVersionMenuClick = React.useCallback(() => {
		playerState.handlePause(playerState.prefPlaying);
	}, [playerState.handlePause, playerState.prefPlaying]);
	const startCurrentSourceIfAllowed = React.useCallback(() => {
		playerState.setProgress({ played: 0 });
		if (suppressInitialQueryAutoplayRef.current) {
			suppressInitialQueryAutoplayRef.current = false;
			playerState.playing.current = false;
			return;
		}
		if (playerState.prefPlaying) {
			playerState.handlePlay();
		} else {
			playerState.playing.current = false;
		}
	}, [playerState.handlePlay, playerState.playing, playerState.prefPlaying, playerState.setProgress]);
	const getPreviewAssetById = (previewAssetId: string) => {
		return previewAssets.find((previewAsset: any) => getPreviewAssetId(previewAsset) === previewAssetId) || null;
	};
	useEffect(() => {
		assetRef.current = asset;
	}, [asset]);
	function handleClose() {
		setOpen(false);
		setAssetNotLoadable(false);
		if (onCloseCallback.current !== null) {
			onCloseCallback.current();
		}
		onCloseCallback.current = null;
		setTimeout(() => {
			handleCloseReset();
		}, 150);
	}
	function handleCloseReset() {
		requestedSign.current = new Set();
		compareRequestedSign.current = new Set();
		curVideoSourceKey.current = "";
		compareVideoSourceKey.current = "";
		curPrefVersion.current = "";
		setAssetId("");
		setVersionId("");
		setVersion({});
		setAsset({});
		setCurrentPreviewAssetId("");
		setCurVideoUrl("");
		setCompareEnabled(false);
		setCompareVersionId("");
		setCompareVersion({});
		setCompareVideoUrl("");
		setQueryComparePreviewAssetId("");
		setPendingQueryTimeSeconds(null);
		suppressInitialQueryAutoplayRef.current = false;
		setInfoState("none");
		setGridState("none");
		setOverscanZoomEnabled(false);
		setVideoFlipped(false);
		setFrameDownloadBusy(false);
		setOverlaySize({ width: 0, height: 0 });
		playerState.resetAutoplayPreference();
	}
	useImperativeHandle(ref, () => ({
		openDialog: (assetIdInput: string, versionIdInput: string = "", previewAssetId: string = "", inputOnCloseCallback: any = () => {}, options: PlayerDialogOpenOptions = {}) => {
			const currentPreviewAssetId = curPreviewAssetIdRef.current;
			const switchingAsset = open && assetIdInput !== assetId;
			const switchingActiveMedia =
				open && (assetIdInput !== assetId || (versionIdInput.length > 0 && versionIdInput !== versionId) || (previewAssetId.length > 0 && previewAssetId !== currentPreviewAssetId) || (versionIdInput.length > 0 && previewAssetId === ""));
			const nextCompareVersionId = String(options?.compareVersionId || "").trim();
			const nextComparePreviewAssetId = String(options?.comparePreviewAssetId || "").trim();
			const nextQueryTimeSeconds = parseQueryTimeSeconds(options?.currentTimeSeconds);
			if (!open) {
				setFilterSelects(false);
				setCurFiltered([]);
				setVideoFlipped(false);
				setFrameDownloadBusy(false);
				playerState.resetAutoplayPreference();
				playerState.setMuted(true);
				playerState.setFioTimingInvalid(false);
				playerState.setProgress({ played: 0 });
				playerState.setCurFrame(0);
				curLclOverwrites.current = {};
				resetCompareState();
			}
			if (switchingAsset) {
				resetCompareState();
			}
			if (switchingActiveMedia) {
				requestedSign.current = new Set();
				unloadCurrentVideo();
				setOverlaySize({ width: 0, height: 0 });
				playerState.setProgress({ played: 0 });
				playerState.setCurFrame(0);
			}
			if (options?.fromQuery) {
				suppressInitialQueryAutoplayRef.current = !open && nextQueryTimeSeconds !== null;
				if (nextCompareVersionId.length) {
					if (!compareEnabled || nextCompareVersionId !== compareVersionId) {
						compareRequestedSign.current = new Set();
						unloadCompareVideo();
						setCompareVersion({});
					}
					setCompareEnabled(true);
					setCompareVersionId(nextCompareVersionId);
					setQueryComparePreviewAssetId(nextComparePreviewAssetId);
				} else {
					resetCompareState();
				}
				if (nextQueryTimeSeconds !== null) {
					setPendingQueryTimeSeconds(nextQueryTimeSeconds);
				} else {
					setPendingQueryTimeSeconds(null);
				}
			}
			onCloseCallback.current = inputOnCloseCallback;
			setCurrentPreviewAssetId(previewAssetId);
			curPrefVersion.current = versionIdInput;
			setVersionId(versionIdInput);
			setAssetId(assetIdInput);
			setOpen(true);
		},
		closeDialog: () => {
			handleClose();
		},
	}));
	function handlePreviewAssetChange(event: any, newValue: string) {
		if (!newValue) {
			return;
		}
		if (curPreviewAssetIdRef.current !== newValue) {
			requestedSign.current = new Set();
			compareRequestedSign.current = new Set();
			setQueryComparePreviewAssetId("");
			unloadCurrentVideo();
			unloadCompareVideo();
			setOverlaySize({ width: 0, height: 0 });
			playerState.setProgress({ played: 0 });
			playerState.setCurFrame(0);
			setCurrentPreviewAssetId(newValue);
			syncPlayerQuery({ f: newValue, cf: null, t: null });
		}
	}
	function handleCompareVersionSelect(nextVersionId: string) {
		if (!nextVersionId) {
			resetCompareState();
			syncPlayerQuery({ cv: null, cf: null });
			return;
		}
		if (compareEnabled && nextVersionId === compareVersionId) {
			return;
		}
		compareRequestedSign.current = new Set();
		setQueryComparePreviewAssetId("");
		unloadCompareVideo();
		setCompareEnabled(true);
		setCompareVersionId(nextVersionId);
		setCompareVersion({});
		syncPlayerQuery({ cv: nextVersionId, cf: null, t: null });
	}
	function handleClearCompare() {
		resetCompareState();
		syncPlayerQuery({ cv: null, cf: null });
	}
	function onVersionsUpdate(evt: any) {
		setVersion(evt);
		const values = evt?.values || {};
		const currentPreviewAssetId = curPreviewAssetIdRef.current;
		if (!currentPreviewAssetId.length) {
			const defaultPreviewAsset = getDefaultPreviewAssetId(values);
			if (defaultPreviewAsset) {
				handlePreviewAssetChange({}, defaultPreviewAsset);
			}
		}
	}
	function requestSignForVersion(versionEntity: any, src_fields: any[]) {
		const versionEntityValues = versionEntity?.values || {};
		const versionEntityId = versionEntity?.id || "";
		const safeSrcFields = (src_fields || [])
			.map((srcField) => {
				const srcPath = normalizeFieldPath(srcField?.src_path);
				return { ...srcField, src_path: srcPath };
			})
			.filter((srcField) => {
				const srcPath = normalizeFieldPath(srcField?.src_path);
				if (!srcPath || isSignedUrlFieldPath(srcPath)) {
					return false;
				}
				const storagePath = getByPath(versionEntityValues, srcPath);
				return isSignableStoragePath(storagePath);
			});
		if (!safeSrcFields.length || !versionEntityId) {
			return Promise.resolve();
		}
		return axios({
			url: `${authController?.extra?.apiUrl}/resign_url`,
			method: "POST",
			headers: { Authorization: `boostToken ${authController?.extra?.user_data?.values?.boost_token}` },
			data: { src_fields: safeSrcFields, version_id: versionEntityId },
		});
	}
	useEffect(() => {
		if (!open) {
			return;
		}
		syncPlayerQuery({ a: assetId, v: versionId, f: curPreviewAssetId, cv: compareEnabled && compareVersionId ? compareVersionId : null, cf: compareEnabled && compareVersionId && comparePreviewAssetId ? comparePreviewAssetId : null });
	}, [open, assetId, versionId, curPreviewAssetId, compareEnabled, compareVersionId, comparePreviewAssetId, syncPlayerQuery]);
	useEffect(() => {
		if (!open) {
			return;
		}
		let rafId = 0;
		let attempts = 0;
		let cleanupVideoListeners: (() => void) | null = null;
		let stopped = false;
		const attach = () => {
			if (stopped) {
				return;
			}
			const video = playerState.playerRef.current?.getInternalPlayer?.();
			if (video?.addEventListener) {
				const handlePlay = () => {
					syncPlayerQuery({ t: null });
				};
				const handlePause = () => {
					commitCurrentTimeToQuery();
				};
				const handleSeeked = () => {
					if (video.paused) {
						commitCurrentTimeToQuery();
					}
				};
				video.addEventListener("play", handlePlay);
				video.addEventListener("pause", handlePause);
				video.addEventListener("seeked", handleSeeked);
				cleanupVideoListeners = () => {
					video.removeEventListener("play", handlePlay);
					video.removeEventListener("pause", handlePause);
					video.removeEventListener("seeked", handleSeeked);
				};
				return;
			}
			attempts += 1;
			if (attempts < 120) {
				rafId = requestAnimationFrame(attach);
			}
		};
		rafId = requestAnimationFrame(attach);
		return () => {
			stopped = true;
			cancelAnimationFrame(rafId);
			if (cleanupVideoListeners) {
				cleanupVideoListeners();
			}
		};
	}, [open, curVideoUrl, compareVideoUrl, shouldSwapComparisonSides, playerState.playerRef, syncPlayerQuery, commitCurrentTimeToQuery]);
	useEffect(() => {
		if (!open || pendingQueryTimeSeconds === null) {
			return;
		}
		let rafId = 0;
		let attempts = 0;
		let cleanupVideoListeners: (() => void) | null = null;
		let stopped = false;
		const applyPendingTime = () => {
			if (stopped) {
				return true;
			}
			const video = playerState.playerRef.current?.getInternalPlayer?.();
			if (!video || typeof video.currentTime === "undefined" || Number(video.readyState) < 1) {
				return false;
			}
			const durationValue = Number(video.duration);
			const targetTime = Number.isFinite(durationValue) && durationValue > 0 ? clampNumber(pendingQueryTimeSeconds, 0, Math.max(0, durationValue - 0.001)) : Math.max(0, pendingQueryTimeSeconds);
			try {
				video.currentTime = targetTime;
				if (Number.isFinite(durationValue) && durationValue > 0) {
					playerState.setProgress?.({ played: targetTime / durationValue });
				}
				setPendingQueryTimeSeconds(null);
				syncPlayerQuery({ t: formatQueryTimeSeconds(targetTime) });
				return true;
			} catch {
				return false;
			}
		};
		const runApplyLoop = () => {
			attempts += 1;
			if (applyPendingTime()) {
				return;
			}
			if (attempts < 120) {
				rafId = requestAnimationFrame(runApplyLoop);
			}
		};
		const attachMetadataListeners = () => {
			const video = playerState.playerRef.current?.getInternalPlayer?.();
			if (video?.addEventListener) {
				video.addEventListener("loadedmetadata", applyPendingTime);
				video.addEventListener("loadeddata", applyPendingTime);
				video.addEventListener("canplay", applyPendingTime);
				return () => {
					video.removeEventListener("loadedmetadata", applyPendingTime);
					video.removeEventListener("loadeddata", applyPendingTime);
					video.removeEventListener("canplay", applyPendingTime);
				};
			}
			return () => {};
		};
		cleanupVideoListeners = attachMetadataListeners();
		rafId = requestAnimationFrame(runApplyLoop);
		return () => {
			stopped = true;
			cancelAnimationFrame(rafId);
			if (cleanupVideoListeners) {
				cleanupVideoListeners();
			}
		};
	}, [open, pendingQueryTimeSeconds, curVideoUrl, compareVideoUrl, shouldSwapComparisonSides, playerState.playerRef, playerState.setProgress, syncPlayerQuery]);
	useEffect(() => {
		if (typeof versionListener.current !== "undefined") {
			versionListener.current();
			versionListener.current = undefined;
		}
		if (!dataSource.listenEntity || !versionId.length) {
			return;
		}
		versionListener.current = dataSource.listenEntity({ path: versionsCollection.path, entityId: versionId, collection: versionsCollection, onUpdate: (evt: any) => onVersionsUpdate(evt) });
		return () => {
			if (typeof versionListener.current !== "undefined") {
				versionListener.current();
				versionListener.current = undefined;
			}
		};
	}, [dataSource, versionId]);
	useEffect(() => {
		if (typeof compareVersionListener.current !== "undefined") {
			compareVersionListener.current();
			compareVersionListener.current = undefined;
		}
		if (!compareEnabled || !dataSource.listenEntity || !compareVersionId.length) {
			setCompareVersion((prev: any) => {
				if (!prev || Object.keys(prev).length === 0) {
					return prev;
				}
				return {};
			});
			return;
		}
		compareVersionListener.current = dataSource.listenEntity({
			path: versionsCollection.path,
			entityId: compareVersionId,
			collection: versionsCollection,
			onUpdate: (evt: any) => {
				setCompareVersion(evt);
			},
		});
		return () => {
			if (typeof compareVersionListener.current !== "undefined") {
				compareVersionListener.current();
				compareVersionListener.current = undefined;
			}
		};
	}, [dataSource, compareEnabled, compareVersionId]);
	useEffect(() => {
		if (typeof curPreviewAssetId === "undefined" || curPreviewAssetId === "") {
			return;
		}
		const currentSourceContext = getPreviewSourceContext(versionValues, curPreviewAssetId);
		if (!currentSourceContext?.id || !currentSourceContext?.isVideo) {
			clearCurrentVideoForNonVideo();
			return;
		}
		const availableVideos = getAvailableVideoSources(versionValues, currentSourceContext);
		const bestVideo = pickBestVideoSource(availableVideos, desiredVideoQuality);
		if (!bestVideo?.source) {
			return;
		}
		const source = bestVideo.source;
		const videoSourceKey = [version?.id || versionId, curPreviewAssetId, currentSourceContext?.provider || "", currentSourceContext?.id || "", source.pathField || source.path || "", desiredVideoQuality].join("|");
		const activeSourceChanged = Boolean(curVideoSourceKey.current && curVideoSourceKey.current !== videoSourceKey);
		if (activeSourceChanged && curVideoUrl.length) {
			unloadCurrentVideo();
		}
		const signedUrlIsFresh = isFreshSignedUrl(source.signedUrl);
		const sourceUrl = signedUrlIsFresh ? source.signedUrl : isHttpUrl(source.path) ? source.path : "";
		const currentUrlStillUsable = isHttpUrl(curVideoUrl) || isFreshSignedUrl(curVideoUrl);
		const alreadyShowingThisSource = curVideoSourceKey.current === videoSourceKey;
		if (sourceUrl) {
			if (alreadyShowingThisSource && curVideoUrl.length && currentUrlStillUsable) {
				return;
			}
			curVideoSourceKey.current = videoSourceKey;
			setCurVideoUrl(sourceUrl);
			startCurrentSourceIfAllowed();
			return;
		}
		if (!isSignableStoragePath(source.path)) {
			return;
		}
		if (!source.pathField || requestedSign.current.has(source.pathField)) {
			return;
		}
		if (!version?.id) {
			return;
		}
		requestedSign.current.add(source.pathField);
		playerState.setBuffering(true);
		requestSignForVersion(version, [{ src_path: source.pathField }]).catch((err) => console.log(err));
	}, [curPreviewAssetId, version, versionId, desiredVideoQuality, curVideoUrl, startCurrentSourceIfAllowed, unloadCurrentVideo, clearCurrentVideoForNonVideo]);
	useEffect(() => {
		if (!compareEnabled || !compareVersionId || !comparePreviewAssetId) {
			unloadCompareVideo();
			return;
		}
		const currentSourceContext = getPreviewSourceContext(compareVersionValues, comparePreviewAssetId);
		if (!currentSourceContext?.id || !currentSourceContext?.isVideo) {
			unloadCompareVideo();
			return;
		}
		const availableVideos = getAvailableVideoSources(compareVersionValues, currentSourceContext);
		const bestVideo = pickBestVideoSource(availableVideos, desiredVideoQuality);
		if (!bestVideo?.source) {
			return;
		}
		const source = bestVideo.source;
		const videoSourceKey = [compareVersion?.id || compareVersionId, comparePreviewAssetId, currentSourceContext?.provider || "", currentSourceContext?.id || "", source.pathField || source.path || "", desiredVideoQuality].join("|");
		const activeSourceChanged = Boolean(compareVideoSourceKey.current && compareVideoSourceKey.current !== videoSourceKey);
		if (activeSourceChanged && compareVideoUrl.length) {
			unloadCompareVideo();
		}
		const signedUrlIsFresh = isFreshSignedUrl(source.signedUrl);
		const sourceUrl = signedUrlIsFresh ? source.signedUrl : isHttpUrl(source.path) ? source.path : "";
		const currentUrlStillUsable = isHttpUrl(compareVideoUrl) || isFreshSignedUrl(compareVideoUrl);
		const alreadyShowingThisSource = compareVideoSourceKey.current === videoSourceKey;
		if (sourceUrl) {
			if (alreadyShowingThisSource && compareVideoUrl.length && currentUrlStillUsable) {
				return;
			}
			compareVideoSourceKey.current = videoSourceKey;
			setCompareVideoUrl(sourceUrl);
			if (shouldSwapComparisonSides) {
				startCurrentSourceIfAllowed();
			}
			return;
		}
		if (!isSignableStoragePath(source.path)) {
			return;
		}
		if (!source.pathField || compareRequestedSign.current.has(source.pathField)) {
			return;
		}
		if (!compareVersion?.id) {
			return;
		}
		compareRequestedSign.current.add(source.pathField);
		requestSignForVersion(compareVersion, [{ src_path: source.pathField }]).catch((err) => console.log(err));
	}, [compareEnabled, compareVersionId, compareVersion, compareVersionValues, comparePreviewAssetId, desiredVideoQuality, compareVideoUrl, unloadCompareVideo, shouldSwapComparisonSides, startCurrentSourceIfAllowed]);
	useEffect(() => {
		if (typeof curPreviewAssetId === "undefined" || curPreviewAssetId === "") {
			return;
		}
		const currentSourceContext = getPreviewSourceContext(versionValues, curPreviewAssetId);
		if (!currentSourceContext?.id || currentSourceContext?.provider !== "image") {
			return;
		}
		const imageSource = getPreferredImageSource(currentSourceContext?.assetInfo);
		if (!imageSource?.pathField) {
			return;
		}
		if (isFreshSignedUrl(imageSource.signedUrl) || isHttpUrl(imageSource.path)) {
			return;
		}
		if (!isSignableStoragePath(imageSource.path)) {
			return;
		}
		if (requestedSign.current.has(imageSource.pathField)) {
			return;
		}
		if (!version?.id) {
			return;
		}
		requestedSign.current.add(imageSource.pathField);
		requestSignForVersion(version, [{ src_path: imageSource.pathField }]).catch((err) => console.log(err));
	}, [curPreviewAssetId, version?.id, versionValues]);
	useEffect(() => {
		if (!compareEnabled || !compareVersionId || !comparePreviewAssetId) {
			return;
		}
		const currentSourceContext = getPreviewSourceContext(compareVersionValues, comparePreviewAssetId);
		if (!currentSourceContext?.id || currentSourceContext?.provider !== "image") {
			return;
		}
		const imageSource = getPreferredImageSource(currentSourceContext?.assetInfo);
		if (!imageSource?.pathField) {
			return;
		}
		if (isFreshSignedUrl(imageSource.signedUrl) || isHttpUrl(imageSource.path)) {
			return;
		}
		if (!isSignableStoragePath(imageSource.path)) {
			return;
		}
		if (compareRequestedSign.current.has(imageSource.pathField)) {
			return;
		}
		if (!compareVersion?.id) {
			return;
		}
		compareRequestedSign.current.add(imageSource.pathField);
		requestSignForVersion(compareVersion, [{ src_path: imageSource.pathField }]).catch((err) => console.log(err));
	}, [compareEnabled, compareVersionId, compareVersion?.id, compareVersionValues, comparePreviewAssetId]);
	function onAssetUpdate(evt: any) {
		if (assetRef.current?.id !== evt?.id) {
			let latestVersionId;
			if (curPrefVersion.current?.length) {
				latestVersionId = curPrefVersion.current;
			} else {
				latestVersionId = evt.values?.versions?.map((v: any) => v?.ref?.id)?.sort((a: any, b: any) => a?.localeCompare(b))?.[0] || "";
			}
			setVersionId(latestVersionId);
			playerState.setShowTC(0);
		}
		setAsset(evt);
	}
	useEffect(() => {
		if (typeof assetListener.current !== "undefined") {
			assetListener.current();
			assetListener.current = undefined;
		}
		if (!dataSource.listenEntity || !assetId.length) {
			return;
		}
		assetListener.current = dataSource.listenEntity({
			path: assetsCollection.path,
			entityId: assetId,
			collection: assetsCollection,
			onUpdate: (evt: any) => onAssetUpdate(evt),
			onError: () => {
				setAssetNotLoadable(true);
			},
		});
		return () => {
			if (typeof assetListener.current !== "undefined") {
				assetListener.current();
				assetListener.current = undefined;
			}
		};
	}, [dataSource, assetId]);
	function toggleInfoState(type?: "compact" | "metadata" | null) {
		if (!controlledVersionMetadata) {
			return;
		}
		setInfoState((prev) => {
			if (type === "compact") {
				return prev === "compact" ? "none" : "compact";
			}
			if (type === "metadata") {
				return prev === "metadata" ? "none" : "metadata";
			}
			if (prev === "none") return "compact";
			if (prev === "compact") return "metadata";
			return "none";
		});
	}
	function toggleGridState() {
		setGridState((prev) => {
			if (effectiveOverscanZoomEnabled) {
				return prev === "none" ? "grid" : "none";
			}
			if (prev === "none") {
				return "grid";
			}
			if (prev === "grid") {
				return overscanPercentage > 0 ? "overscangrid" : "none";
			}
			if (prev === "overscangrid") {
				return "none";
			}
			return "none";
		});
	}
	function toggleOverscanZoom() {
		setOverscanZoomEnabled((prev) => !prev);
	}
	function toggleVideoFlip() {
		setVideoFlipped((prev) => !prev);
	}

	async function resolveCurrentSourceDownloadUrl() {
		const currentProvider = sourceContext?.provider || "";
		const currentAssetId = version?.values?.asset?.id || "";
		const currentVersionId = version?.id || versionId || "";
		const currentPreviewAssetId = curPreviewAssetId || "";

		if (!currentAssetId || !currentVersionId || !currentPreviewAssetId || !currentProvider || currentProvider === "none") {
			throw new Error("Missing source video information for frame download.");
		}

		const response = await axios({
			url: `${authController?.extra?.apiUrl}/download/${currentAssetId}/${currentVersionId}/${currentPreviewAssetId}/src`,
			method: "GET",
			params: {
				provider: currentProvider,
			},
			headers: {
				Authorization: `boostToken ${authController?.extra?.user_data?.values?.boost_token}`,
			},
		});

		const sourceUrl = response?.data?.url;

		if (!response?.data?.success || !isHttpUrl(sourceUrl)) {
			throw new Error("Could not resolve a playable source video URL for frame download.");
		}

		return sourceUrl;
	}

	async function handleDownloadCurrentFrame(format: FrameDownloadFormat) {
		if (showComparisonStage || !previewAssetIsVideo || frameDownloadBusy) {
			return;
		}

		const targetTime = getCurrentPlayerTimeSeconds(playerState);

		setFrameDownloadBusy(true);

		try {
			const sourceUrl = await resolveCurrentSourceDownloadUrl();
			const blob = await createFrameBlobFromSourceVideo(sourceUrl, targetTime, format);
			const filename = buildFrameDownloadFilename(asset?.values?.name, versionId, curPreviewAssetId, targetTime, format);

			downloadBlob(blob, filename);
		} catch (error) {
			const message = getFrameDownloadErrorMessage(error);

			console.log(error);
			window.alert(message);
			throw error;
		} finally {
			setFrameDownloadBusy(false);
		}
	}

	function toggleQuality() {
		if (desiredVideoQuality === 0 || desiredVideoQuality === 2160) {
			setDesiredVideoQuality(540);
		} else {
			setDesiredVideoQuality(2160);
		}
	}
	function toggleOverlay() {
		setOverlayVisible((prev) => !prev);
	}
	function goToNextPreviewAsset(direction: number) {
		const selectedIds = getCurSelected();
		const allVisiblePreviewAssetIds = previewAssets
			?.filter((previewAsset: any) => {
				const previewAssetId = getPreviewAssetId(previewAsset);
				const selectionId = getPreviewAssetSelectionId(previewAsset);
				return !filterSelects || (curFiltered || []).indexOf(selectionId) > -1 || selectedIds.indexOf(selectionId) > -1 || previewAssetId === curPreviewAssetId;
			})
			.map((previewAsset: any) => getPreviewAssetId(previewAsset));
		const curIndex = allVisiblePreviewAssetIds.indexOf(curPreviewAssetId);
		if (curIndex === -1) {
			return;
		}
		let nextIndex = curIndex + direction;
		if (nextIndex < 0) {
			nextIndex = allVisiblePreviewAssetIds?.length - 1;
		}
		if (nextIndex > allVisiblePreviewAssetIds?.length - 1) {
			nextIndex = 0;
		}
		handlePreviewAssetChange({}, allVisiblePreviewAssetIds[nextIndex]);
	}
	function goToNextVersion(direction: number) {
		const allVersions = [...(asset?.values?.versions || [])].sort((a: any, b: any) => b?.ref?.id?.localeCompare(a?.ref?.id));
		const currentVersion = allVersions.find((x: any) => x?.ref?.id === versionId);
		const currentIndex = allVersions.indexOf(currentVersion);
		let desiredIndex = currentIndex + direction;
		if (desiredIndex > allVersions.length - 1) {
			desiredIndex = 0;
		} else if (desiredIndex < 0) {
			desiredIndex = allVersions.length - 1;
		}
		const nextVersion = allVersions?.[desiredIndex] || {};
		if (nextVersion?.ref?.id === versionId) {
			return;
		}
		authController?.extra?.playerDialogHandleOpen(assetId, nextVersion?.ref?.id, "");
	}
	function markPreviewAssetSelected(evt: any, previewAssetId: string, markSelected: boolean) {
		evt.preventDefault();
		evt.stopPropagation();
		const previewAsset = getPreviewAssetById(previewAssetId);
		const currentSourceContext = getPreviewSourceContext(versionValues, previewAssetId);
		const provider = currentSourceContext?.provider && currentSourceContext.provider !== "none" ? currentSourceContext.provider : previewAsset?.provider || "fio";
		const providerUsesUploadId = provider === "coconut" || provider === "image";
		const selectedAssetId = providerUsesUploadId
			? getPreviewAssetUploadId(previewAsset) || currentSourceContext?.upload_id || currentSourceContext?.uploadId || currentSourceContext?.coconut_upload_id || currentSourceContext?.image_upload_id || currentSourceContext?.id || previewAssetId
			: getPreviewAssetSelectionId(previewAsset) || currentSourceContext?.fio_asset_id || currentSourceContext?.id || previewAssetId;
		if (!selectedAssetId) {
			console.warn("Could not mark preview asset selected. Missing selected asset id.", { previewAssetId, provider, previewAsset, currentSourceContext });
			return;
		}
		curLclOverwrites.current[`${selectedAssetId}`] = { marked: markSelected, date: Date.now() };
		const body = {
			asset_id: assetId,
			version_id: versionId,
			mark_selected: markSelected,
			provider,
			preview_asset_id: previewAssetId,
			fio_asset_id: provider === "fio" ? selectedAssetId : undefined,
			upload_id: provider === "coconut" || provider === "image" ? selectedAssetId : undefined,
			coconut_upload_id: provider === "coconut" ? selectedAssetId : undefined,
			image_upload_id: provider === "image" ? selectedAssetId : undefined,
		};
		setDummy(Date.now());
		return axios({ url: `${authController?.extra?.apiUrl}/version/edit/selects`, method: "POST", headers: { Authorization: `boostToken ${authController?.extra?.user_data?.values?.boost_token}` }, data: body });
	}
	function handleToggleFilterSelects(evt: any) {
		evt.preventDefault();
		evt.stopPropagation();
		const shouldShowSelected = !filterSelects;
		setFilterSelects(shouldShowSelected);
		if (shouldShowSelected) {
			let selectedPreviewAssets = getCurSelected();
			if (selectedPreviewAssets.length < 1) {
				selectedPreviewAssets = previewAssets.map((previewAsset: any) => getPreviewAssetSelectionId(previewAsset)).filter(Boolean);
			}
			const currentPreviewAsset = getPreviewAssetById(curPreviewAssetId);
			const currentSelectionId = getPreviewAssetSelectionId(currentPreviewAsset) || curPreviewAssetId;
			if (selectedPreviewAssets.indexOf(currentSelectionId) === -1) {
				const nextVisiblePreviewAsset = previewAssets.find((previewAsset: any) => selectedPreviewAssets.indexOf(getPreviewAssetSelectionId(previewAsset)) > -1);
				if (nextVisiblePreviewAsset) {
					setCurrentPreviewAssetId(getPreviewAssetId(nextVisiblePreviewAsset));
				}
			}
			setCurFiltered(selectedPreviewAssets);
		}
	}
	function getCurSelected() {
		const values = versionValues || {};
		let intermedSelected = [...toIdArray(values.selected_fio_assets), ...toIdArray(values.selected_coconut_assets), ...toIdArray(values.selected_image_assets), ...toIdArray(values.selected_preview_assets)];
		intermedSelected = intermedSelected.filter(Boolean).filter((id: any, index: number, arr: any[]) => arr.indexOf(id) === index);
		Object.entries(curLclOverwrites.current).forEach(([key, value]: any) => {
			const timeDistance = Date.now() - value.date;
			if (timeDistance < 5000 && value.marked && intermedSelected.indexOf(key) === -1) {
				intermedSelected.push(key);
			} else if (timeDistance < 5000 && !value.marked && intermedSelected.indexOf(key) !== -1) {
				intermedSelected.splice(intermedSelected.indexOf(key), 1);
			}
		});
		return intermedSelected;
	}
	function handleKeyDown(e: any) {
		const preventAll = (evt: any) => {
			(document.activeElement as HTMLElement)?.blur();
			evt.stopPropagation();
			evt.preventDefault();
		};
		if (e.key === "PageUp") {
			preventAll(e);
			goToNextVersion(-1);
		} else if (e.key === "PageDown") {
			preventAll(e);
			goToNextVersion(1);
		}
		if (e.key.toLowerCase() === "o") {
			preventAll(e);
			toggleOverlay();
		}
		if (e.key.toLowerCase() === "i") {
			preventAll(e);
			toggleInfoState("compact");
		}
		if (e.key.toLowerCase() === "m") {
			preventAll(e);
			toggleInfoState("metadata");
		}
		if (e.key.toLowerCase() === "g") {
			preventAll(e);
			toggleGridState();
		}
		if (e.key.toLowerCase() === "z") {
			preventAll(e);
			toggleOverscanZoom();
		}
		if (e.keyCode === 70) {
			preventAll(e);
			handleToggleFilterSelects(e);
		}
		if (e.keyCode === 81) {
			preventAll(e);
			toggleQuality();
		}
		if (e.keyCode === 48 || e.keyCode === 96) {
			preventAll(e);
			markPreviewAssetSelected(e, curPreviewAssetId, false);
		}
		if (e.keyCode === 49 || e.keyCode === 97) {
			preventAll(e);
			markPreviewAssetSelected(e, curPreviewAssetId, true);
		}
		if (e.shiftKey && e.key === "ArrowLeft") {
			preventAll(e);
			goToNextPreviewAsset(-1);
		}
		if (e.shiftKey && e.key === "ArrowRight") {
			preventAll(e);
			goToNextPreviewAsset(1);
		}
		if (!(shouldSwapComparisonSides ? comparePreviewAssetIsVideo : previewAssetIsVideo)) {
			return;
		}
		if (e.key.toLowerCase() === "h") {
			preventAll(e);
			toggleVideoFlip();
		} else if (e.key === " " || e.code === "Space" || e.keyCode === 32) {
			preventAll(e);
			playerState.handleTogglePlayPause();
		} else if (e.keyCode === 75) {
			preventAll(e);
			playerState.handleTogglePlayPause();
		} else if (!e.shiftKey && e.key === "ArrowLeft") {
			preventAll(e);
			playerState.moveFrame(-1);
		} else if (e.key === ",") {
			preventAll(e);
			playerState.moveFrame(-25);
		} else if (!e.shiftKey && e.key === "ArrowRight") {
			preventAll(e);
			playerState.moveFrame(1);
		} else if (e.key === ".") {
			preventAll(e);
			playerState.moveFrame(25);
		} else if (e.key === "ArrowDown") {
			preventAll(e);
			playerState.handlePause();
			if (playerState.fioTimingInvalid || !(shouldSwapComparisonSides ? compareFrameAccurateTimingAvailable : frameAccurateTimingAvailable)) {
				try {
					playerState.playerRef.current.getInternalPlayer().currentTime = playerState.duration;
				} catch {}
				return;
			}
			playerState.goToFrame(playerState.getLastFrame());
		} else if (e.key === "ArrowUp") {
			preventAll(e);
			if (playerState.fioTimingInvalid || !(shouldSwapComparisonSides ? compareFrameAccurateTimingAvailable : frameAccurateTimingAvailable)) {
				try {
					playerState.playerRef.current.getInternalPlayer().currentTime = 0;
				} catch {}
				return;
			}
			playerState.goToFrame(0);
		}
	}
	useEffect(() => {
		const controlledPreviewAssetIsVideo = shouldSwapComparisonSides ? comparePreviewAssetIsVideo : previewAssetIsVideo;
		const controlledPreviewAssetInfoForMeasure = shouldSwapComparisonSides ? comparePreviewAssetInfo : previewAssetInfo;
		const controlledVersionMetadataForMeasure = shouldSwapComparisonSides ? compareVersionMetadata : versionMetadata;
		if (!controlledPreviewAssetIsVideo) {
			setOverlaySize((prev) => {
				if (prev.width === 0 && prev.height === 0) {
					return prev;
				}
				return { width: 0, height: 0 };
			});
			return;
		}
		let rafId = 0;
		let timeoutId: ReturnType<typeof setTimeout> | null = null;
		let resizeObserver: ResizeObserver | null = null;
		let attempts = 0;
		const maxAttempts = 90;
		const roundSize = (value: number) => Math.round(value * 100) / 100;
		const measureOverlaySize = (): boolean => {
			const mediaViewport = document.querySelector<HTMLElement>('[data-tree-player-media-viewport="true"]');
			const viewportRect = mediaViewport?.getBoundingClientRect?.();
			const player = playerState.playerRef.current?.getInternalPlayer?.();
			const videoWidth = Number(player?.videoWidth) || 0;
			const videoHeight = Number(player?.videoHeight) || 0;
			const viewportWidth = Number(viewportRect?.width) || 0;
			const viewportHeight = Number(viewportRect?.height) || 0;
			if (!viewportWidth || !viewportHeight) {
				return false;
			}
			let videoAspectRatio = videoWidth > 0 && videoHeight > 0 ? videoWidth / videoHeight : 0;
			if (!videoAspectRatio || !Number.isFinite(videoAspectRatio)) {
				videoAspectRatio = getPreviewAspectRatio(controlledPreviewAssetInfoForMeasure, controlledVersionMetadataForMeasure);
			}
			if (!videoAspectRatio || !Number.isFinite(videoAspectRatio)) {
				videoAspectRatio = 16 / 9;
			}
			const viewportAspectRatio = viewportWidth / viewportHeight;
			if (!viewportAspectRatio || !Number.isFinite(viewportAspectRatio)) {
				return false;
			}
			let calcSize = { width: 0, height: 0 };
			if (viewportAspectRatio > videoAspectRatio) {
				calcSize = { width: viewportHeight * videoAspectRatio, height: viewportHeight };
			} else {
				calcSize = { width: viewportWidth, height: viewportWidth * (1 / videoAspectRatio) };
			}
			const nextSize = { width: roundSize(calcSize.width), height: roundSize(calcSize.height) };
			setOverlaySize((prev) => {
				const widthDiff = Math.abs(prev.width - nextSize.width);
				const heightDiff = Math.abs(prev.height - nextSize.height);
				if (widthDiff < 0.5 && heightDiff < 0.5) {
					return prev;
				}
				return nextSize;
			});
			return true;
		};
		const runMeasureLoop = () => {
			attempts += 1;
			const measured = measureOverlaySize();
			if ((!measured || attempts < 8) && attempts < maxAttempts) {
				rafId = requestAnimationFrame(runMeasureLoop);
			}
		};
		const attachVideoMetadataListener = () => {
			const player = playerState.playerRef.current?.getInternalPlayer?.();
			if (player?.addEventListener) {
				player.addEventListener("loadedmetadata", measureOverlaySize);
				player.addEventListener("loadeddata", measureOverlaySize);
				player.addEventListener("resize", measureOverlaySize);
				return () => {
					player.removeEventListener("loadedmetadata", measureOverlaySize);
					player.removeEventListener("loadeddata", measureOverlaySize);
					player.removeEventListener("resize", measureOverlaySize);
				};
			}
			return () => {};
		};
		const mediaViewport = document.querySelector<HTMLElement>('[data-tree-player-media-viewport="true"]');
		if (mediaViewport && typeof ResizeObserver !== "undefined") {
			resizeObserver = new ResizeObserver(() => {
				measureOverlaySize();
			});
			resizeObserver.observe(mediaViewport);
		}
		const removeVideoListeners = attachVideoMetadataListener();
		rafId = requestAnimationFrame(runMeasureLoop);
		timeoutId = setTimeout(() => {
			measureOverlaySize();
		}, 100);
		return () => {
			cancelAnimationFrame(rafId);
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
			removeVideoListeners();
		};
	}, [windowSize, curPreviewAssetId, curVideoUrl, compareVideoUrl, previewAssetIsVideo, comparePreviewAssetIsVideo, shouldSwapComparisonSides, gridState, overscanZoom, previewAssetInfo, comparePreviewAssetInfo, versionMetadata, compareVersionMetadata, playerState.playerRef]);
	const selectedPreviewAssetIds = getCurSelected();
	const primaryComparisonMedia = React.useMemo(
		() => ({ label: versionId || "Primary", version, versionId: version?.id || versionId, previewAssetId: curPreviewAssetId, previewAssetIsVideo, previewAssetInfo, videoUrl: curVideoUrl }),
		[version, versionId, curPreviewAssetId, previewAssetIsVideo, previewAssetInfo, curVideoUrl]
	);
	const compareComparisonMedia = React.useMemo(
		() => ({
			label: compareVersionId || "Compare",
			version: compareVersion,
			versionId: compareVersion?.id || compareVersionId,
			previewAssetId: comparePreviewAssetId,
			previewAssetIsVideo: comparePreviewAssetIsVideo,
			previewAssetInfo: comparePreviewAssetInfo,
			videoUrl: compareVideoUrl,
		}),
		[compareVersion, compareVersionId, comparePreviewAssetId, comparePreviewAssetIsVideo, comparePreviewAssetInfo, compareVideoUrl]
	);
	const stagePrimaryMedia = shouldSwapComparisonSides ? compareComparisonMedia : primaryComparisonMedia;
	const stageCompareMedia = shouldSwapComparisonSides ? primaryComparisonMedia : compareComparisonMedia;
	const controlsVersion = shouldSwapComparisonSides ? compareVersion : version;
	const controlsVersionId = shouldSwapComparisonSides ? compareVersionId : versionId;
	const controlsPreviewAssetId = shouldSwapComparisonSides ? comparePreviewAssetId : curPreviewAssetId;
	const controlsPreviewAssetIsVideo = shouldSwapComparisonSides ? comparePreviewAssetIsVideo : previewAssetIsVideo;
	const controlsPreviewAssetInfo = shouldSwapComparisonSides ? comparePreviewAssetInfo : previewAssetInfo;
	const controlsFrameAccurateTimingAvailable = shouldSwapComparisonSides ? compareFrameAccurateTimingAvailable : frameAccurateTimingAvailable;
	const controlsVersionMetadata = shouldSwapComparisonSides ? compareVersionMetadata : versionMetadata;
	const showComparisonStage = !assetNotLoadable && compareEnabled && compareVersionId.length > 0 && (previewAssetIsVideo || comparePreviewAssetIsVideo);

	return (
		<Dialog
			open={open}
			maxWidth={false}
			sx={{ borderRadius: "15px", boxShadow: 24, outline: "none", ".MuiPaper-root": { margin: 0 } }}
			PaperProps={{ sx: { m: 0, ml: 0, height: "100%", width: "100%", overflow: "hidden", backgroundColor: "black", backgroundImage: "none" } }}
			scroll={"paper"}
			onClose={handleClose}
			aria-labelledby="scroll-dialog-title"
			aria-describedby="scroll-dialog-description"
			onKeyDown={handleKeyDown}
		>
			<DialogTitle sx={{ position: "relative", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "background.paper", color: "text.primary", p: 0, m: 0, height: "50px" }} component="h6">
				<IconButton sx={{ zIndex: 6000, flex: "1 1 0px", display: "flex", justifyContent: "start", alignItems: "start" }} size={"small"} disableRipple onClick={handleClose}>
					<ArrowBackIcon sx={{ ml: 2 }} fontSize="inherit" />
				</IconButton>
				{assetNotLoadable ? (
					<Box sx={{ backgroundColor: "error.main", border: 0, borderColor: "rgba(255,255,255,0.05)", pl: 1, pr: 1, borderRadius: 2, display: "flex", alignItems: "center", typography: "body1" }}>
						<b>Not available</b>
					</Box>
				) : (
					<Box sx={{ background: "rgba(0,0,0,.5)", border: 0, borderColor: "rgba(255,255,255,0.05)", pl: 1, pr: 1, borderRadius: 2, display: "flex", alignItems: "center", typography: "body1" }}>
						<b>{asset?.values?.name}</b>
					</Box>
				)}
				<MemoVersionMenu onClick={handleVersionMenuClick} asset={asset} version={version} />
				<CompareVersionMenu
					asset={asset}
					primaryVersionId={versionId}
					compareVersionId={compareVersionId}
					compareEnabled={compareEnabled}
					onClick={handleVersionMenuClick}
					onSelectVersion={handleCompareVersionSelect}
					onClearCompare={handleClearCompare}
				/>
				<Box sx={{ zIndex: 6000, flex: "1" }} />
			</DialogTitle>
			<Divider />
			<DialogContent
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					background: "black!important",
					height: 1000,
					maxHeight: "95%",
					overflowY: "hidden",
					"&::-webkit-scrollbar": { background: "rgba(0,0,0,0)", width: "10px" },
					overflow: "hidden",
					"&::-webkit-scrollbar-thumb": { background: "rgba(125,125,125,1)", borderRadius: "10px" },
				}}
			>
				{showComparisonStage ? (
					<PlayerComparisonStage
						primary={stagePrimaryMedia}
						compare={stageCompareMedia}
						playerState={playerState}
						layout="side-by-side"
						syncEnabled={true}
						infoState={infoState}
						gridState={gridState}
						overscanZoomEnabled={overscanZoomEnabled}
						swapVisualSides={shouldSwapComparisonSides}
						videoFlipped={videoFlipped}
					/>
				) : (
					<PlayerStage
						assetNotLoadable={assetNotLoadable}
						version={version}
						curPreviewAssetId={curPreviewAssetId}
						previewAssetIsVideo={previewAssetIsVideo}
						previewAssetInfo={previewAssetInfo}
						curVideoUrl={curVideoUrl}
						playerState={playerState}
						infoState={infoState}
						gridState={gridState}
						overlaySize={overlaySize}
						versionMetadata={versionMetadata}
						overscanPercentage={resolveOverscanPercentageFromVersionMetadata(versionMetadata)}
						overscanZoom={overscanZoom}
						overscanZoomEnabled={effectiveOverscanZoomEnabled}
						videoFlipped={videoFlipped}
					/>
				)}
				{!assetNotLoadable ? (
					<PlayerControls
						isSwap={shouldSwapComparisonSides}
						version={controlsVersion}
						versionId={controlsVersionId}
						curPreviewAssetId={controlsPreviewAssetId}
						previewAssetIsVideo={controlsPreviewAssetIsVideo}
						previewAssetInfo={controlsPreviewAssetInfo}
						frameAccurateTimingAvailable={controlsFrameAccurateTimingAvailable}
						playerState={playerState}
						infoState={infoState}
						gridState={gridState}
						versionMetadata={controlsVersionMetadata}
						desiredVideoQuality={desiredVideoQuality}
						filterSelects={filterSelects}
						overscanZoomEnabled={overscanZoomEnabled}
						canToggleOverscanZoom={canToggleOverscanZoom}
						videoFlipped={videoFlipped}
						canDownloadCurrentFrame={!showComparisonStage && previewAssetIsVideo}
						isDownloadingFrame={frameDownloadBusy}
						onToggleGridState={toggleGridState}
						onToggleInfoState={toggleInfoState}
						onToggleFilterSelects={handleToggleFilterSelects}
						onToggleQuality={toggleQuality}
						onToggleOverscanZoom={toggleOverscanZoom}
						onToggleVideoFlip={toggleVideoFlip}
						onDownloadCurrentFrame={handleDownloadCurrentFrame}
						comparePreviewAssetInfo={comparePreviewAssetInfo}
					/>
				) : null}
			</DialogContent>
			<Divider />
			<DialogActions sx={{ height: "150px", backgroundColor: "background.paper" }}>
				<PreviewAssetTabs
					version={version}
					previewAssets={previewAssets}
					curPreviewAssetId={curPreviewAssetId}
					filterSelects={filterSelects}
					curFiltered={curFiltered}
					selectedPreviewAssetIds={selectedPreviewAssetIds}
					onChange={handlePreviewAssetChange}
					onToggleSelected={markPreviewAssetSelected}
				/>
			</DialogActions>
		</Dialog>
	);
});
